/**
 * The till.
 *
 * `complete` is the only write that matters, and it carries an idempotency key
 * the *caller* owns. That is deliberate: the key must survive a retry, so it has
 * to be created once when the basket is first submitted and reused for every
 * attempt after that. A key generated inside this function would be new on each
 * retry, which is precisely the bug the header exists to prevent.
 */

import { api, NetworkError } from "@/api/client";

import { localCatalogueRepository } from "./localCatalogueRepository";

export type PaymentMethod = "CASH" | "CARD" | "EFT" | "MOBILE" | "VOUCHER" | "ACCOUNT";

export interface TillOption {
  id: string;
  name: string;
  code: string;
  section_id: string;
  section_name: string;
  branch_name: string;
}

export interface SaleLine {
  id: string;
  line_number: number;
  product: string;
  description: string;
  unit_code: string;
  quantity: string;
  unit_price: string;
  discount_amount: string;
  tax_rate: string;
  tax_amount: string;
  net_amount: string;
  line_total: string;
  /** What was chosen at the till, snapshotted onto the line. */
  modifiers: { id: string; name: string; price_delta: string }[];
}

export interface SalePayment {
  id: string;
  method: PaymentMethod;
  amount: string;
  tendered: string;
  change: string;
  reference: string;
}

export interface Sale {
  id: string;
  receipt_number: string;
  status: string;
  business_date: string;
  subtotal: string;
  discount_total: string;
  tax_total: string;
  rounding_adjustment: string;
  total: string;
  amount_paid: string;
  change_given: string;
  vat_mode: "NONE" | "INCLUSIVE" | "EXCLUSIVE";
  vat_rate: string;
  note: string;
  completed_at: string | null;
  created_at: string;
  cashier_name: string;
  till_name: string;
  item_count: number;
  lines: SaleLine[];
  payments: SalePayment[];
  /** True when the server replayed a previous submission rather than selling again. */
  duplicate?: boolean;
}

export interface SaleSummaryRow {
  id: string;
  receipt_number: string;
  status: string;
  total: string;
  item_count: number;
  payment_method: string;
  cashier_name: string;
  completed_at: string | null;
}

export interface SalesSummary {
  today: {
    sales_total: string;
    transaction_count: number;
    average_sale: string;
  };
  recent: SaleSummaryRow[];
  held: SaleSummaryRow[];
}

export interface BasketItem {
  product_id: string;
  quantity: string;
}

export interface TillProduct {
  id: string;
  name: string;
  selling_price: string;
  tax_rate: string;
  category: string | null;
  image_url: string | null;
  image_thumb_url: string | null;
  /**
   * On the shelf at this counter. Present because the design puts it on the
   * tile, and because a cashier reaching for the last one should know it is the
   * last one — this platform allows selling into negative stock, so the figure
   * is information rather than a gate.
   */
  stock_on_hand: string;
  /** For a made-up product: how many the ingredients allow. `null` otherwise. */
  makeable: string | null;
}

const TILLS_KEY = "smartspaza.tills";

export const salesRepository = {
  /**
   * The tills this person may sell at, falling back to the last list seen.
   *
   * Without the fallback, a till restarted with no signal had no till list: no
   * till code for its receipts (they printed `T00-…`, the same on every
   * terminal) and, on a machine not paired to a till, no till to put the sale
   * on at all. A till's code and section do not change from one day to the
   * next, so yesterday's list is the right answer offline.
   */
  tills: async (): Promise<TillOption[]> => {
    try {
      const tills = await api.get<TillOption[]>("/sales/tills/");
      try {
        globalThis.localStorage?.setItem(TILLS_KEY, JSON.stringify(tills));
      } catch {
        // Storage full or blocked; the online answer still stands.
      }
      return tills;
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error;
      try {
        const cached = globalThis.localStorage?.getItem(TILLS_KEY);
        if (cached) return JSON.parse(cached) as TillOption[];
      } catch {
        // Fall through to the original error.
      }
      throw error;
    }
  },

  /**
   * The product grid.
   *
   * Served by the sales app rather than the catalogue one because it carries the
   * stock figure, and `apps.products` may not depend on `apps.inventory` — a
   * catalogue that cannot list itself until the stock tables exist is a worse
   * system. It is also gated on `sales.create` rather than `inventory.view`, so
   * a cashier can actually see it.
   */
  gridProducts: async (
    params: { q?: string | undefined; category?: string | undefined } = {},
  ): Promise<TillProduct[]> => {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.category) query.set("category", params.category);
    try {
      // Five seconds, not the usual fifteen. On a signal too weak to answer in
      // five, the cashier is better served by the local copy than by a spinner.
      return await api.get<TillProduct[]>(`/sales/products/?${query.toString()}`, {
        timeoutMs: 5_000,
      });
    } catch (error) {
      if (error instanceof NetworkError) return localCatalogueRepository.gridProducts(params);
      throw error;
    }
  },

  summary: (section?: string) =>
    api.get<SalesSummary>(`/sales/summary/${section ? `?section=${section}` : ""}`),

  sale: (id: string) => api.get<Sale>(`/sales/${id}/`),

  /**
   * Ring up a sale.
   *
   * `idempotencyKey` and `saleId` both come from the caller and both must be
   * stable across retries — the key so the server replays, the id so a retry
   * that somehow lost its key still collides on the primary key rather than
   * creating a second sale.
   */
  complete: (
    input: {
      sale_id: string;
      till_id: string;
      items: BasketItem[];
      payments: { method: PaymentMethod; amount: string; tendered?: string; reference?: string }[];
      note?: string;
      client_created_at?: string;
      /** Who it is for. Required for a sale on account. */
      customer_id?: string;
    },
    idempotencyKey: string,
  ) => api.post<Sale>("/sales/", input, { idempotencyKey }),

  hold: (input: { sale_id: string; till_id: string; items: BasketItem[]; note?: string }) =>
    api.post<Sale>("/sales/hold/", input),

  void: (id: string, reason: string) => api.post<Sale>(`/sales/${id}/void/`, { reason }),

  /** What is still refundable on a sale, line by line. */
  refundable: (id: string) => api.get<Refundable>(`/sales/${id}/refundable/`),

  /**
   * Give money back.
   *
   * The caller owns the idempotency key, as with a sale: a refund is money
   * leaving the drawer, and a retry after a dropped response must not send it
   * twice. Note what is absent — an amount. The figures come from the original
   * sale's own lines.
   */
  refund: (
    id: string,
    input: {
      lines: { line_id: string; quantity: string; restock: boolean }[];
      reason: string;
      method?: PaymentMethod;
      /** The till paying the money out, when it is not the one that took it. */
      till_id?: string;
      /** A manager approving it, where the shop asks for approval. */
      approver_staff_code?: string;
      approver_pin?: string;
    },
    idempotencyKey: string,
  ) => api.post<Sale>(`/sales/${id}/refund/`, input, { idempotencyKey }),

  /**
   * Sales whose receipt number contains this text, newest first.
   *
   * For a refund days later: the customer holds the paper, and the number on it
   * is what the cashier types.
   */
  findByReceipt: async (receipt: string): Promise<Sale[]> => {
    const body = await api.get<{ results: Sale[] } | Sale[]>(
      `/sales/?receipt=${encodeURIComponent(receipt.trim())}`,
    );
    return Array.isArray(body) ? body : body.results;
  },
};

export interface RefundableLine {
  line_id: string;
  /** Position on the sale, from 1. Optional because older servers lack it. */
  line_number?: number;
  description: string;
  sold: string;
  refundable: string;
  unit_price: string;
}

export interface Refundable {
  sale_id: string;
  status: string;
  lines: RefundableLine[];
}
